// Edit a group (club). Admins only — RLS enforces it server-side, the UI
// checks the viewer's ACTIVE OWNER/ADMIN member row to fail fast with a
// friendly message.
import { useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { gqlRequest } from "../lib/gql";
import {
  activitiesToWire,
  CLUB_DETAIL,
  UPDATE_CLUB,
  type ClubDetailResult,
  type UpdateClubResult,
} from "../lib/queries";
import { useAuth } from "../lib/auth";
import {
  ACTIVITIES,
  ACTIVITY_ICONS,
  CLUB_JOIN_POLICIES,
  CLUB_VISIBILITIES,
  type Activity,
  type ClubJoinPolicy,
  type ClubVisibility,
} from "../lib/format";
import { useT } from "../lib/i18n";
import { LoginGate } from "../components/LoginGate";
import {
  Chip,
  EmptyState,
  ErrorBox,
  Field,
  Select,
  Spinner,
  TextArea,
  TextInput,
} from "../components/ui";
import { Button } from "@/components/ui/button";

export function EditGroupPage({ id }: { id: string }) {
  return (
    <LoginGate action="gateAction.editGroup" next={`/group/${id}/edit`}>
      <EditGroupLoader id={id} />
    </LoginGate>
  );
}

function EditGroupLoader({ id }: { id: string }) {
  const { personId } = useAuth();
  const { t } = useT();

  const { data, isLoading, error } = useQuery({
    queryKey: ["club", id],
    queryFn: () => gqlRequest<ClubDetailResult>(CLUB_DETAIL, { id }),
  });

  if (isLoading) return <Spinner />;
  if (error) return <ErrorBox error={error} />;
  const club = data?.club;
  if (!club) return <EmptyState>{t("group.notFound")}</EmptyState>;

  // The viewer's row is in the detail's member list whenever they can edit
  // at all (members always see the member list of their own club).
  const myRow = club.clubMembersByClubId.nodes.find((m) => m.personId === personId);
  const isAdmin = myRow?.status === "ACTIVE" && (myRow.role === "OWNER" || myRow.role === "ADMIN");
  if (!isAdmin) return <EmptyState>{t("editGroup.adminsOnly")}</EmptyState>;

  return <EditGroupForm club={club} />;
}

function EditGroupForm({ club }: { club: NonNullable<ClubDetailResult["club"]> }) {
  const { t, labelizeT } = useT();
  const [, navigate] = useLocation();
  const queryClient = useQueryClient();

  const [name, setName] = useState(club.name);
  const [description, setDescription] = useState(club.description ?? "");
  const [visibility, setVisibility] = useState<ClubVisibility>(club.visibility);
  const [joinPolicy, setJoinPolicy] = useState<ClubJoinPolicy>(club.joinPolicy);
  const [activities, setActivities] = useState<Activity[]>(club.activities ?? []);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const save = useMutation({
    mutationFn: () =>
      gqlRequest<UpdateClubResult>(UPDATE_CLUB, {
        id: club.id,
        patch: {
          name: name.trim(),
          description: description.trim() || null,
          visibility,
          joinPolicy,
          activities: activities.length ? activitiesToWire(activities) : null,
        },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["club", club.id] });
      queryClient.invalidateQueries({ queryKey: ["my-club-memberships"] });
      navigate(`/group/${club.id}`);
    },
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    const errors: Record<string, string> = {};
    if (!name.trim()) errors.name = t("newGroup.errName");
    setFieldErrors(errors);
    if (Object.keys(errors).length === 0) save.mutate();
  }

  return (
    <div className="mx-auto max-w-xl p-4">
      <h1 className="mb-5 text-2xl font-semibold tracking-tight text-foreground">{t("group.edit")}</h1>
      <form onSubmit={onSubmit} className="space-y-4">
        <Field label={t("form.name")} error={fieldErrors.name}>
          <TextInput value={name} onChange={(e) => setName(e.target.value)} />
        </Field>

        <Field label={t("form.description")}>
          <TextArea rows={4} value={description} onChange={(e) => setDescription(e.target.value)} />
        </Field>

        <Field label={t("form.activities")}>
          <div className="flex flex-wrap gap-2">
            {ACTIVITIES.map((a) => {
              const Icon = ACTIVITY_ICONS[a];
              const active = activities.includes(a);
              return (
                <Chip
                  key={a}
                  active={active}
                  onClick={() =>
                    setActivities(active ? activities.filter((x) => x !== a) : [...activities, a])
                  }
                >
                  <span className="inline-flex items-center gap-1.5">
                    <Icon aria-hidden className="size-3.5" />
                    {labelizeT(a)}
                  </span>
                </Chip>
              );
            })}
          </div>
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label={t("form.visibility")}>
            <Select value={visibility} onChange={(e) => setVisibility(e.target.value as ClubVisibility)}>
              {CLUB_VISIBILITIES.map((v) => (
                <option key={v} value={v}>
                  {labelizeT(v)}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t("group.joinPolicy")}>
            <Select value={joinPolicy} onChange={(e) => setJoinPolicy(e.target.value as ClubJoinPolicy)}>
              {CLUB_JOIN_POLICIES.map((p) => (
                <option key={p} value={p}>
                  {labelizeT(p)}
                </option>
              ))}
            </Select>
          </Field>
        </div>
        <p className="text-xs text-muted-foreground">{t("newGroup.visibilityHint")}</p>

        {save.error != null && <ErrorBox error={save.error} />}

        <Button type="submit" disabled={save.isPending} className="w-full py-2.5">
          {save.isPending ? t("common.saving") : t("edit.submit")}
        </Button>
      </form>
    </div>
  );
}
